import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  MessageCircle,
  HelpCircle,
  Book,
  Shield,
  Smartphone,
  Target,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Send,
  AlertCircle,
  Zap,
  Settings,
  Lock,
  RefreshCw
} from 'lucide-react';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:support@goalhive.com?subject=${encodeURIComponent(`[Support] ${formData.subject}`)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`)}`;
    window.location.href = mailtoLink;
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  const faqs = [
    {
      category: 'Getting Started',
      icon: Zap,
      questions: [
        {
          q: 'How do I create a GoalHive account?',
          a: 'Download GoalHive from the App Store, tap "Get Started," and sign up using your email address or continue with Apple/Google Sign-In. Follow the onboarding steps to set up your profile and create your first goal.'
        },
        {
          q: 'Is GoalHive free to use?',
          a: 'GoalHive offers a free tier with core features including goal creation, progress tracking, and community access. Premium features like advanced AI coaching, unlimited goals, and detailed analytics are available through our subscription plans.'
        },
        {
          q: 'What devices are supported?',
          a: 'GoalHive is currently available exclusively on iOS devices (iPhone) running iOS 15.0 or later. Android support is planned for a future release.'
        }
      ]
    },
    {
      category: 'Goals & Tracking',
      icon: Target,
      questions: [
        {
          q: 'How do I create a new goal?',
          a: 'Tap the "+" button on your home screen, select a goal category (Fitness, Learning, Career, etc.), enter your goal details, and choose your target date. Our AI coach will help you break down your goal into actionable steps.'
        },
        {
          q: 'Can I share goals with friends?',
          a: 'Yes! GoalHive is built for social accountability. You can create a shared goal and invite your friends to join.'
        },
        {
          q: 'How does the progress tracking work?',
          a: 'Log your progress through daily check-ins and timer sessions. GoalHive automatically tracks your streaks, calculates completion percentages, and provides visual progress charts to keep you motivated.'
        },
        {
          q: 'Can I change a goal\'s privacy setting after creating it?',
          a: 'Goals can be set to Private or Public at the time of creation. Once a goal is created, its privacy setting cannot be changed. If you need a different privacy setting, you can create a new goal with the desired setting.'
        },
        {
          q: 'What happens if I miss a day?',
          a: 'Life happens! Missing a day won\'t reset your overall progress. Your AI coach will send encouraging reminders and help you get back on track. Streaks may reset, but your total progress and achievements are always preserved.'
        }
      ]
    },
    {
      category: 'Account & Settings',
      icon: Settings,
      questions: [
        {
          q: 'How do I change my notification settings?',
          a: 'Go to Settings > Notifications to customize your reminder schedule, choose notification types (check-ins, social updates, achievements).'
        },
        {
          q: 'How do I update my profile information?',
          a: 'Go to Settings and tap "Update Profile" to change your name, bio, profile photo, or other details. Changes save automatically.'
        },
        {
          q: 'How do I delete my account?',
          a: 'Go to Settings, scroll to the bottom, and tap "Delete Account." Please note that account deletion is permanent and will remove all your goals, progress data, and social connections. You can also email support@goalhive.com to request account deletion.'
        }
      ]
    },
    {
      category: 'Privacy & Security',
      icon: Lock,
      questions: [
        {
          q: 'Who can see my goals and progress?',
          a: 'You control your privacy when creating each goal. Goals can be set to Private (only you can see) or Public (visible to the community). Choose your preferred privacy setting when creating a new goal.'
        },
        {
          q: 'Does GoalHive sell my data?',
          a: 'No, we never sell your personal data. We only share data with service providers essential to operating GoalHive (hosting, analytics) and as required by law. See our Privacy Policy for complete details.'
        },
        {
          q: 'How can I request deletion of my data?',
          a: 'You can delete your account and all associated data by going to Settings and tapping "Delete Account" at the bottom, or by emailing support@goalhive.com. We will process your request promptly.'
        }
      ]
    },
    {
      category: 'Technical Issues',
      icon: RefreshCw,
      questions: [
        {
          q: 'The app is crashing or freezing. What should I do?',
          a: 'First, ensure you have the latest version from the App Store. Try closing and reopening the app. If issues persist, restart your iPhone. Still having trouble? Contact us at support@goalhive.com with your device model and iOS version.'
        },
        {
          q: 'My progress isn\'t showing correctly.',
          a: 'Check your internet connection and try pulling down to refresh on your home screen. If issues continue, try signing out and back in. Contact support if the problem persists.'
        },
        {
          q: 'Push notifications aren\'t working.',
          a: 'Go to your iPhone\'s Settings > GoalHive > Notifications and ensure they\'re enabled. In the GoalHive app, check Settings > Notifications to verify your preferences. Try disabling and re-enabling notifications.'
        },
        {
          q: 'How do I report a bug?',
          a: 'We appreciate bug reports! Email support@goalhive.com with a description of the issue, steps to reproduce it, your iPhone model, iOS version, and screenshots if possible.'
        }
      ]
    },
    {
      category: 'Billing & Subscriptions',
      icon: Shield,
      questions: [
        {
          q: 'How do I upgrade to Premium?',
          a: 'Go to Settings > Subscription to view plans and subscribe. Payment is processed securely through the App Store.'
        },
        {
          q: 'How do I cancel my subscription?',
          a: 'Subscriptions are managed through Apple. Go to your iPhone\'s Settings > [Your Name] > Subscriptions > GoalHive > Cancel Subscription. You\'ll retain Premium access until the end of your billing period.'
        },
        {
          q: 'How do I request a refund?',
          a: 'Since subscriptions are processed by Apple, refund requests must go through Apple\'s support at reportaproblem.apple.com. You can also contact us at support@goalhive.com and we\'ll assist you with the process.'
        },
        {
          q: 'Will I lose my data if I cancel Premium?',
          a: 'No, your data is always preserved. If you cancel Premium, you\'ll retain access to your goals and historical data but may lose access to Premium-only features like advanced analytics and unlimited goals.'
        }
      ]
    }
  ];

  const supportOptions = [
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Get help via email within 24-48 hours',
      action: 'support@goalhive.com',
      actionType: 'email'
    },
    {
      icon: MessageCircle,
      title: 'In-App Feedback',
      description: 'Submit feedback through the app - we\'d love to hear from you! Your feedback helps us improve and create a better experience for everyone.',
      action: 'Open GoalHive > Settings > Submit Feedback',
      actionType: 'text'
    },
    {
      icon: Book,
      title: 'Documentation',
      description: 'Browse our Privacy Policy and Terms of Service',
      action: 'Links below',
      actionType: 'text'
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0a0a0a]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#e5e5e5]/50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain" />
              <span className="text-lg font-semibold tracking-tight text-[#0a0a0a]">GoalHive</span>
            </Link>
            <div className="hidden md:flex items-center">
              <div className="flex items-center gap-1 p-1 bg-[#f5f5f5] rounded-full">
                <Link to="/support" className="px-4 py-2 text-sm font-medium bg-white text-[#0a0a0a] rounded-full shadow-sm transition-all duration-200">Support</Link>
                <Link to="/privacy" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#0a0a0a] hover:bg-white rounded-full transition-all duration-200">Privacy</Link>
                <Link to="/terms" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#0a0a0a] hover:bg-white rounded-full transition-all duration-200">Terms</Link>
              </div>
            </div>
            <Link
              to="/"
              className="h-10 px-5 bg-[#0a0a0a] text-white text-sm font-medium rounded-full hover:bg-[#171717] hover:scale-105 transition-all duration-200 shadow-lg shadow-black/10 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-14 h-14 bg-[#0a0a0a] rounded-2xl flex items-center justify-center">
              <HelpCircle className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
            <span className="text-[#0a0a0a]">Support </span>
            <span className="text-[#0a0a0a]">Center</span>
          </h1>
          <p className="text-xl text-[#666] mb-4">
            We're here to help you achieve your goals. Find answers or reach out to our team.
          </p>
          <p className="text-sm text-[#999]">
            Average response time: <span className="text-[#0a0a0a] font-semibold">Under 24 hours</span>
          </p>
        </div>
      </section>

      {/* Quick Support Options */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {supportOptions.map((option, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-white border border-[#e5e5e5] hover:border-[#d4d4d4] hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#0a0a0a] rounded-xl flex items-center justify-center mb-6">
                  <option.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-[#0a0a0a]">{option.title}</h3>
                <p className="text-[#666] mb-4">{option.description}</p>
                {option.actionType === 'email' ? (
                  <a
                    href={`mailto:${option.action}`}
                    className="text-[#0a0a0a] hover:text-[#666] font-semibold flex items-center gap-2 transition-colors"
                  >
                    {option.action}
                    <Send className="w-4 h-4" />
                  </a>
                ) : (
                  <p className="text-[#0a0a0a] font-semibold">{option.action}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact Banner */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="p-6 rounded-2xl bg-[#f5f5f5] border border-[#e5e5e5] flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0a0a0a] rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-[#0a0a0a]">Urgent Issue?</h3>
                <p className="text-[#666] text-sm">For account security concerns or critical bugs, email us directly.</p>
              </div>
            </div>
            <a
              href="mailto:support@goalhive.com?subject=URGENT: "
              className="px-6 py-3 bg-[#0a0a0a] text-white rounded-full font-semibold hover:bg-[#171717] hover:scale-105 transition-all duration-200 shadow-lg shadow-black/10"
            >
              Contact Urgent Support
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-[#0a0a0a]">
              Frequently Asked Questions
            </h2>
            <p className="text-[#666]">Find quick answers to common questions</p>
          </div>

          <div className="space-y-6">
            {faqs.map((category, catIndex) => (
              <div key={catIndex} className="rounded-2xl bg-white border border-[#e5e5e5] overflow-hidden">
                <div className="p-6 border-b border-[#e5e5e5] flex items-center gap-4 bg-[#fafafa]">
                  <div className="w-10 h-10 bg-[#0a0a0a] rounded-lg flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0a0a0a]">{category.category}</h3>
                </div>
                <div className="divide-y divide-[#e5e5e5]">
                  {category.questions.map((faq, faqIndex) => {
                    const globalIndex = `${catIndex}-${faqIndex}`;
                    return (
                      <div key={faqIndex} className="p-6">
                        <button
                          onClick={() => toggleFaq(globalIndex)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <span className="font-semibold text-[#0a0a0a] pr-4">{faq.q}</span>
                          {openFaq === globalIndex ? (
                            <ChevronUp className="w-5 h-5 text-[#0a0a0a] flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#999] flex-shrink-0" />
                          )}
                        </button>
                        {openFaq === globalIndex && (
                          <div className="mt-4 text-[#666] leading-relaxed pl-0 md:pl-4 border-l-2 border-[#0a0a0a] ml-0 md:ml-2">
                            <p className="pl-4">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-[#0a0a0a]">
              Still Need Help?
            </h2>
            <p className="text-[#666]">Send us a message and we'll get back to you within 24-48 hours</p>
          </div>

          <div className="p-8 md:p-12 rounded-3xl bg-white border border-[#e5e5e5]">
            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-[#0a0a0a]">Message Sent!</h3>
                <p className="text-[#666]">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#0a0a0a] mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#fafafa] border border-[#e5e5e5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0a0a0a] focus:border-transparent text-[#0a0a0a] placeholder-[#999] transition-all"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0a0a0a] mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#fafafa] border border-[#e5e5e5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0a0a0a] focus:border-transparent text-[#0a0a0a] placeholder-[#999] transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0a0a0a] mb-2">
                    Subject *
                  </label>
                  <select
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-[#fafafa] border border-[#e5e5e5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0a0a0a] focus:border-transparent text-[#0a0a0a]"
                  >
                    <option value="">Select a topic...</option>
                    <option value="Account Issue">Account Issue</option>
                    <option value="Technical Problem">Technical Problem</option>
                    <option value="Billing Question">Billing Question</option>
                    <option value="Feature Request">Feature Request</option>
                    <option value="Bug Report">Bug Report</option>
                    <option value="Privacy Concern">Privacy Concern</option>
                    <option value="Account Deletion Request">Account Deletion Request</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0a0a0a] mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-[#fafafa] border border-[#e5e5e5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0a0a0a] focus:border-transparent text-[#0a0a0a] placeholder-[#999] resize-none transition-all"
                    placeholder="Please describe your issue or question in detail..."
                  />
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className="text-sm text-[#999]">
                    * Required fields
                  </p>
                  <button
                    type="submit"
                    className="px-8 py-4 bg-[#0a0a0a] text-white rounded-full font-semibold hover:bg-[#171717] hover:scale-105 transition-all duration-200 shadow-lg shadow-black/10 flex items-center gap-2"
                  >
                    Send Message
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* App Info Section */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-white border border-[#e5e5e5]">
              <Smartphone className="w-8 h-8 text-[#0a0a0a] mb-4" />
              <h4 className="font-bold mb-2 text-[#0a0a0a]">Platform</h4>
              <p className="text-[#666] text-sm">iOS (iPhone only)</p>
              <p className="text-[#999] text-xs mt-1">Requires iOS 15.0 or later</p>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-[#e5e5e5]">
              <Clock className="w-8 h-8 text-[#0a0a0a] mb-4" />
              <h4 className="font-bold mb-2 text-[#0a0a0a]">Support Hours</h4>
              <p className="text-[#666] text-sm">Monday - Friday</p>
              <p className="text-[#999] text-xs mt-1">Response within 24-48 hours</p>
            </div>
            <div className="p-6 rounded-2xl bg-white border border-[#e5e5e5]">
              <Shield className="w-8 h-8 text-[#0a0a0a] mb-4" />
              <h4 className="font-bold mb-2 text-[#0a0a0a]">Data Controller</h4>
              <p className="text-[#666] text-sm">GoalHive LLC</p>
              <p className="text-[#999] text-xs mt-1">United States</p>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Contact CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="p-12 rounded-3xl bg-[#0a0a0a]">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4 text-white">
              Can't Find What You Need?
            </h3>
            <p className="text-lg mb-8 text-white/70">
              Our support team is always ready to help you succeed.
            </p>
            <a
              href="mailto:support@goalhive.com"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#0a0a0a] font-semibold rounded-full hover:bg-[#f5f5f5] hover:scale-105 transition-all duration-200 shadow-lg"
            >
              <Mail className="w-5 h-5" />
              support@goalhive.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain" />
              <div>
                <span className="text-lg font-semibold tracking-tight text-[#0a0a0a]">GoalHive</span>
                <p className="text-[#999] text-sm">&copy; 2025 GoalHive LLC. All rights reserved.</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#666]">
              <Link to="/privacy" className="hover:text-[#0a0a0a] transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#0a0a0a] transition-colors">Terms of Service</Link>
              <Link to="/" className="hover:text-[#0a0a0a] transition-colors">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Support;
